import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  InputAdornment,
  Snackbar,
  Typography,
  Box,
  Paper,
  Fade,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  AttachMoney,
  Email,
  Person,
  Notes,
  Receipt,
  Close,
  Refresh,
  Send,
  Calculate,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Description,
  Warning,
} from '@mui/icons-material';
import API from '../../../API'; // Adjust path to your API file
import { handleAvatarError } from '../../../Utils';
import profilePhoto from '../../../Components/globalAssets/ppfLogo.png';

function AgencyInvoiceDialog({ open, onClose, onInvoiceCreated }) {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Form state
  const [creatorList, setCreatorList] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [interestRate, setInterestRate] = useState(10);
  const [poNumber, setPoNumber] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [amountDue, setAmountDue] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [clientCompanyName, setClientCompanyName] = useState('');
  const [errors, setErrors] = useState({});
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Steps configuration
  const steps = [
    { label: 'Creator', icon: <Person /> },
    { label: 'Details', icon: <Description /> },
    { label: 'Review', icon: <CheckCircle /> },
  ];

  // Generate PO number
  const generatePONumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `AGY-${timestamp}-${random}`;
  };

  // Initialize dialog
  useEffect(() => {
    if (open) {
      setPoNumber(generatePONumber());
      setSelectedCreator(null);
      setClientName('');
      setClientEmail('');
      setClientNotes('');
      setItemDescription('');
      setAmountDue('');
      setInterestRate(10);
      setEmailStatus(null);
      setClientCompanyName('');
      setErrors({});
      setCurrentStep(0);
    }
  }, [open]);

  // Fetch creators on mount
  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const data = await API.creators.list({ only_agency: true });
        setCreatorList(data.map(c => ({
          ...c,
          label: c.display_name || c.creator,
        })));
      } catch (error) {
        console.error('Error fetching creators:', error);
        showSnackbar('Error loading creators', 'error');
      }
    };

    if (open) {
      fetchCreators();
    }
  }, [open]);

  // Calculate fees and net amount
  const { blitzFee, netToReceive } = useMemo(() => {
    const amount = parseFloat(amountDue) || 0;
    const rate = interestRate;
    const fee = (amount * rate) / 100;
    const net = amount - fee;

    return {
      blitzFee: fee,
      netToReceive: Math.max(0, net),
    };
  }, [amountDue, interestRate]);

  // Navigation
  const goToStep = (step) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(step);
      setIsTransitioning(false);
    }, 150);
  };

  const nextStep = () => currentStep < steps.length - 1 && goToStep(currentStep + 1);
  const prevStep = () => currentStep > 0 && goToStep(currentStep - 1);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    const processedValue = field === 'clientEmail' ? value.toLowerCase() : value;
    
    switch (field) {
      case 'clientEmail':
        setClientEmail(processedValue);
        break;
      case 'clientName':
        setClientName(value);
        break;
      case 'clientNotes':
        setClientNotes(value);
        break;
      case 'poNumber':
        setPoNumber(value);
        break;
      case 'itemDescription':
        setItemDescription(value);
        break;
      case 'amountDue':
        setAmountDue(value);
        break;
      default:
        break;
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Check if email exists in system
  const handleCheckEmail = async () => {
    if (!clientEmail || !clientEmail.includes('@')) return;
    
    setEmailLoading(true);
    try {
      const response = await API.creatorConnect.fetchUserInfo(clientEmail);
      if (response?.found) {
        const userData = response.user;
        setClientName(`${userData.first_name} ${userData.last_name}`);
        setInterestRate(userData.company_interest_rate ?? 10);
        setClientCompanyName(userData.company_name || '');
        setEmailStatus('registered');
        showSnackbar('Client found! Company rate applied.', 'success');
      } else {
        setInterestRate(10);
        setEmailStatus('new-user');
        setClientCompanyName('');
        showSnackbar('New client - default rate applied.', 'info');
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      showSnackbar('Error checking client information', 'error');
      setEmailStatus('error');
    } finally {
      setEmailLoading(false);
    }
  };

  // Auto-check email when it changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientEmail) {
        handleCheckEmail();
      } else {
        setEmailStatus(null);
        setInterestRate(10);
        setClientCompanyName('');
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [clientEmail]);

  // Validation per step
  const canProceedFromStep = (step) => {
    switch (step) {
      case 0:
        return selectedCreator && !emailLoading;
      case 1:
        return clientEmail && clientName && poNumber && amountDue && parseFloat(amountDue) > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  // Handle step navigation
  const handleNextStep = () => {
    if (currentStep === steps.length - 1) {
      handleCreateInvoice();
    } else if (canProceedFromStep(currentStep)) {
      nextStep();
    } else {
      showSnackbar('Please complete all required fields', 'error');
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedCreator) newErrors.creator = 'Creator is required';
    if (!clientEmail) newErrors.clientEmail = 'Client email is required';
    else if (!clientEmail.includes('@')) newErrors.clientEmail = 'Invalid email format';
    if (!clientName.trim()) newErrors.clientName = 'Client name is required';
    if (!amountDue) newErrors.amountDue = 'Amount is required';
    else if (parseFloat(amountDue) <= 0) newErrors.amountDue = 'Amount must be greater than 0';
    else if (parseFloat(amountDue) > 100000) newErrors.amountDue = 'Amount too large';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!validateForm()) {
      showSnackbar('Please fix the errors above', 'error');
      return;
    }

    setLoading(true);
    try {
      const amount = parseFloat(amountDue);
      
      const invoiceData = {
        creator_id: selectedCreator.creator,
        po_number: poNumber,
        amount_due: amount,
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        notes: itemDescription.trim() || null,
        client_notes: clientNotes.trim() || null,
        interest_rate: interestRate,
        blitzpay: true,
        unique_code: poNumber,
        payout_date: new Date().toISOString(),
      };

      const response = await API.agency.createInvoice(invoiceData);
      
      showSnackbar('Invoice created successfully!', 'success');
      
      // Reset form
      setSelectedCreator(null);
      setClientName('');
      setClientEmail('');
      setClientNotes('');
      setItemDescription('');
      setAmountDue('');
      setPoNumber(generatePONumber());
      setInterestRate(10);
      setEmailStatus(null);
      setClientCompanyName('');
      setCurrentStep(0);

      // Notify parent component
      if (onInvoiceCreated) {
        onInvoiceCreated(response.invoice || response);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating invoice:', error);
      showSnackbar(error.message || 'Failed to create invoice', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Email status renderer
  const renderEmailStatus = () => {
    switch (emailStatus) {
      case 'registered':
        return (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <CheckCircle fontSize="small" />
              Client found - company rate applied
              <Chip label={`${interestRate}%`} size="small" variant="outlined" />
              {clientCompanyName && (
                <Chip label={clientCompanyName} size="small" variant="outlined" color="info" />
              )}
            </Box>
          </Alert>
        );
      case 'new-user':
        return (
          <Alert severity="warning" sx={{ mt: 2 }}>
            New client — they'll receive signup instructions
          </Alert>
        );
      case 'error':
        return (
          <Alert severity="error" sx={{ mt: 2 }}>
            Unable to verify client — please try again
          </Alert>
        );
      default:
        return null;
    }
  };

  // Step content renderers
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <Person sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Select Creator
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose the creator you're invoicing on behalf of
                </Typography>
              </Box>

              <Stack spacing={3}>
                <Autocomplete
                  options={creatorList}
                  getOptionLabel={(option) => option.label || ''}
                  value={selectedCreator}
                  onChange={(_, newValue) => setSelectedCreator(newValue)}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={option.pfphref || profilePhoto}
                        onError={handleAvatarError(profilePhoto)}
                        sx={{ width: 32, height: 32 }}
                      >
                        {(option.label || '')[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{option.creator}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Creator"
                      variant="outlined"
                      fullWidth
                      required
                      autoFocus
                      error={!!errors.creator}
                      helperText={errors.creator}
                      InputProps={{
                        ...params.InputProps,
                        sx: { fontSize: '1.05rem', py: 1.5 }
                      }}
                    />
                  )}
                />

                {selectedCreator && (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={selectedCreator.pfphref || profilePhoto}
                        onError={handleAvatarError(profilePhoto)}
                        sx={{ width: 64, height: 64 }}
                      >
                        {(selectedCreator.label || '')[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{selectedCreator.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          @{selectedCreator.creator}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'info.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <Description sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Client & Invoice Details
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter client information and invoice specifics
                </Typography>
              </Box>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    Client Information
                  </Typography>
                  
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Client Email Address"
                      type="email"
                      value={clientEmail}
                      onChange={(e) => handleFieldChange('clientEmail', e.target.value)}
                      error={!!errors.clientEmail}
                      helperText={errors.clientEmail}
                      autoFocus
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color={errors.clientEmail ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                        endAdornment: emailLoading && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: '1.05rem', py: 1.5 }
                      }}
                    />

                    {renderEmailStatus()}

                    <TextField
                      fullWidth
                      label="Client Full Name"
                      value={clientName}
                      onChange={(e) => handleFieldChange('clientName', e.target.value)}
                      error={!!errors.clientName}
                      helperText={errors.clientName}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color={errors.clientName ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: '1.05rem', py: 1.5 }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Client Notes (Optional)"
                      multiline
                      rows={2}
                      value={clientNotes}
                      onChange={(e) => handleFieldChange('clientNotes', e.target.value)}
                      placeholder="Add any information about the client..."
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                    Invoice Details
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="PO Number"
                        value={poNumber}
                        onChange={(e) => handleFieldChange('poNumber', e.target.value)}
                        error={!!errors.poNumber}
                        helperText={errors.poNumber}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Receipt />
                            </InputAdornment>
                          ),
                          sx: { fontSize: '1.05rem', py: 1.5 }
                        }}
                      />
                      <Tooltip title="Generate new PO number">
                        <IconButton 
                          onClick={() => handleFieldChange('poNumber', generatePONumber())}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <TextField
                      fullWidth
                      type="number"
                      label="Invoice Amount"
                      value={amountDue}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (Number(value) >= 0 && Number(value) <= 100000)) {
                          handleFieldChange('amountDue', value);
                        }
                      }}
                      error={!!errors.amountDue}
                      helperText={errors.amountDue}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney color={errors.amountDue ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: '1.25rem', py: 1.5 }
                      }}
                    />

                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Invoice Description"
                      value={itemDescription}
                      onChange={(e) => handleFieldChange('itemDescription', e.target.value)}
                      placeholder="Describe the work or service..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <Notes color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>

                {amountDue && (
                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                        Payment Preview
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Invoice Amount:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          ${parseFloat(amountDue || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Blitz Fee ({interestRate}%):</Typography>
                        <Typography color="error.main">
                          -${blitzFee.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Typography sx={{ fontWeight: 700, color: 'success.main' }}>
                          Creator Receives:
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: 'success.main', fontSize: '1.1rem' }}>
                          ${netToReceive.toFixed(2)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>
          </Fade>
        );

      case 2:
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <CheckCircle sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Review & Submit
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Review the invoice before submitting
                </Typography>
              </Box>

              <Stack spacing={3}>
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Creator Information</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={selectedCreator?.pfphref || profilePhoto}
                        onError={handleAvatarError(profilePhoto)}
                        sx={{ width: 48, height: 48 }}
                      >
                        {(selectedCreator?.label || '')[0]}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{selectedCreator?.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          @{selectedCreator?.creator}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Client Information</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Name:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{clientName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Email:</Typography>
                        <Typography>{clientEmail}</Typography>
                      </Box>
                    </Box>
                    {clientCompanyName && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">Company:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{clientCompanyName}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Invoice Details</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">PO Number:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{poNumber}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Amount:</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                          ${parseFloat(amountDue).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    {itemDescription && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">Description:</Typography>
                        <Typography>{itemDescription}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      <Calculate sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Payment Summary
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Invoice Amount:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          ${parseFloat(amountDue).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Interest Rate:</Typography>
                        <Chip label={`${interestRate}%`} size="small" color="primary" variant="outlined" />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="error.main">Blitz Fee:</Typography>
                        <Typography color="error.main">-${blitzFee.toFixed(2)}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" color="success.main">Creator Receives:</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          ${netToReceive.toFixed(2)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '700px',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt />
            <Typography variant="h6" component="div">
              Create Invoice for Creator
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 3 }}>
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {steps.map((stepConfig, index) => (
              <Step key={stepConfig.label}>
                <StepLabel 
                  StepIconComponent={({ active, completed }) => (
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                        color: completed || active ? 'white' : 'grey.600'
                      }}
                    >
                      {completed ? <CheckCircle sx={{ fontSize: 20 }} /> : stepConfig.icon}
                    </Avatar>
                  )}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {stepConfig.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent sx={{ px: 3, minHeight: 400 }}>
          {renderStepContent()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'space-between' }}>
          <Button 
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={prevStep}
            disabled={currentStep === 0 || loading}
            sx={{ minWidth: 100 }}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={onClose} 
              variant="outlined"
              sx={{ minWidth: 100 }}
              disabled={loading}
            >
              Cancel
            </Button>
            
            {currentStep === steps.length - 1 ? (
              <Button 
                variant="contained" 
                onClick={handleNextStep}
                disabled={loading || !canProceedFromStep(currentStep)}
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                sx={{ 
                  minWidth: 160,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {loading ? 'Creating...' : 'Create Invoice'}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNextStep}
                disabled={!canProceedFromStep(currentStep) || loading || emailLoading}
                sx={{ minWidth: 100 }}
              >
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AgencyInvoiceDialog;